/**
 * @fileoverview FlowGrid storage utilities.
 * @module utils/storage
 *
 * @description
 * Reads visitor identity and UTM attribution data from the same
 * cookie / localStorage / sessionStorage keys used by the FlowGrid
 * tracking scripts (track.js & flowgrid_oss_client_sdk.js).
 *
 * These functions are **read-only** — the tracking scripts remain the
 * single writer. The SDK only consumes these values.
 */

// ============================================================================
// COOKIE HELPERS
// ============================================================================

/**
 * Read a cookie value by name.
 * Returns `null` when the cookie is absent or document.cookie is unavailable.
 */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

// ============================================================================
// VISITOR IDENTITY
// ============================================================================

/**
 * Resolve the visitor ID written by track.js / flowgrid_oss_client_sdk.js.
 *
 * Storage locations (checked in order):
 *   1. cookie  `visitor_id`
 *   2. localStorage `visitor_id`
 *
 * Returns `null` when neither source contains a value (e.g. tracking
 * script hasn't run yet, or running server-side).
 */
export function getVisitorId(): string | null {
  try {
    return getCookie('visitor_id') || localStorage.getItem('visitor_id');
  } catch {
    // localStorage unavailable (SSR, privacy mode, etc.)
    return getCookie('visitor_id');
  }
}

/**
 * Resolve the session ID written by track.js.
 *
 * Storage: `sessionStorage("session_id")`
 */
export function getSessionId(): string | null {
  try {
    return sessionStorage.getItem('session_id');
  } catch {
    return null;
  }
}

/**
 * Resolve the web_id written by track.js.
 *
 * Storage: `localStorage("web_id")`
 */
export function getWebId(): string | null {
  try {
    return localStorage.getItem('web_id');
  } catch {
    return null;
  }
}

// ============================================================================
// UTM / ATTRIBUTION
// ============================================================================

export interface FlowGridUTM {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  ref?: string;
  source?: string;
  via?: string;
}

/**
 * Read UTM & attribution parameters persisted by
 * flowgrid_oss_client_sdk.js as `__flowgrid_*` cookies.
 */
export function getUTM(): FlowGridUTM {
  return {
    utmSource: getCookie('__flowgrid_utm_source') || undefined,
    utmMedium: getCookie('__flowgrid_utm_medium') || undefined,
    utmCampaign: getCookie('__flowgrid_utm_campaign') || undefined,
    utmTerm: getCookie('__flowgrid_utm_term') || undefined,
    utmContent: getCookie('__flowgrid_utm_content') || undefined,
    ref: getCookie('__flowgrid_ref') || undefined,
    source: getCookie('__flowgrid_source') || undefined,
    via: getCookie('__flowgrid_via') || undefined,
  };
}

export interface FlowGridUTMHistoryEntry {
  timestamp: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

/**
 * Read the full UTM history array persisted by flowgrid_oss_client_sdk.js.
 *
 * Checks localStorage first, then the `utm_history` cookie.
 */
export function getUTMHistory(): FlowGridUTMHistoryEntry[] {
  try {
    const raw =
      localStorage.getItem('utm_history') ||
      getCookie('utm_history');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
